/*
 * Copyright (c) 2025 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import {
  greaterThan,
  maxStringLengthValidator,
  minNumber,
  OmitRule,
  requiredNumber,
  requiredObject,
  requiredString,
  ValidatorSchema
} from '@irian-ro/util-forms';
import { isDateTimeWithTimezoneInPast } from '@irian-ro/date-utils';
import { TimeWindowDtoModel } from '@well/common-libs/time-management/domain-model';
import { ValidationErrors } from '@angular/forms';
import { ShipmentConfig } from './shipment-request-dto.validators';
import { arePickupAndDeliveryTimeWindowsValid } from './abstract-shipment-common.validators';
import { MAXIMUM_DEFAULT_STRING_INPUT_LENGTH } from '@irian-ro/constants';
import { PackageDtoModel } from '@well/delivery-libs/packages/domain';
import { HandoverDtoModel } from '@well/delivery-libs/handover/domain';
import { ProductCustomsClearanceDtoModel } from '@well/product-customs-clearance/domain';
import { ShipmentVehicleDtoModel } from '../shipment-vehicle-dto.model';

// Cross validator function type - matches ValidationFunction from @irian-ro/util-forms
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type CrossValidator<T> = (value: T | null | undefined, ...otherArgs: any[]) => ValidationErrors | null;

/**
 * Base interface for shipment validation that works with both ShipmentDtoModel and ShipmentRequestDtoModel
 */
export interface ShipmentValidatable {
  packages?: PackageDtoModel[] | null;
  pickup?: HandoverDtoModel | null;
  delivery?: HandoverDtoModel | null;
  productCustomsClearanceDTOS?: ProductCustomsClearanceDtoModel[] | null;
  shipmentVehicleDTO?: ShipmentVehicleDtoModel | null;
}

/**
 * Cross validator for package count exceeded
 */
const packageCountExceededValidator = (
  shipment: ShipmentValidatable,
  availablePackageCount: number,
  listShipmentEnabled: boolean
): ValidationErrors | null => {
  if (listShipmentEnabled && availablePackageCount < 0) {
    return { packageCountExceeded: true };
  } else if (listShipmentEnabled) {
    return null;
  }

  const totalPackagesForCurrentShipment = Object.values(shipment?.packages || {}).reduce(
    (acc, pkg) => acc + (pkg?.pieces || 0),
    0
  );

  return totalPackagesForCurrentShipment > availablePackageCount ? { packageCountExceeded: true } : null;
};

/**
 * Base class for shipment DTO validation.
 * Can be extended to add custom cross validators for specific use cases.
 */
export class ShipmentDtoValidatorsClass {
  protected config: ShipmentConfig;

  constructor(config: ShipmentConfig) {
    this.config = config;
  }

  /**
   * Returns the validator schema. Call this method to get fresh schema with current config values.
   */
  getSchema(): ValidatorSchema<ShipmentValidatable> {
    return {
      pickup: this.getPickupValidators(),
      delivery: this.getDeliveryValidators(),
      packages: this.getPackagesValidators(),
      productCustomsClearanceDTOS: this.getProductCustomsClearanceValidators(),
      shipmentVehicleDTO: this.getShipmentVehicleValidators(),
      $omit: this.getOmitRules(),
      // Cast needed because validator return types are inferred as specific objects but schema expects ValidationErrors
      $cross: this.getCrossValidators() as ValidatorSchema<ShipmentValidatable>['$cross'],
    };
  }

  /**
   * Override in subclasses to add additional cross validators.
   */
  protected getCrossValidators() {
    return [
      (shipment: ShipmentValidatable) => arePickupAndDeliveryTimeWindowsValid(shipment),
      (shipment: ShipmentValidatable) => {
        return this.config.isPreShipment()
          ? null
          : shipment?.packages && Object.keys(shipment.packages).length
            ? null
            : { packageRequired: true };
      },
      (shipment: ShipmentValidatable) => {
        return shipment?.pickup && ((shipment.pickup as any).namedLocationVO || shipment.pickup.address)
          ? null
          : { pickupLocationRequired: true };
      },
      (shipment: ShipmentValidatable) => {
        return shipment?.delivery && ((shipment.delivery as any).namedLocationVO || shipment.delivery.address)
          ? null
          : { deliveryLocationRequired: true };
      },
      (shipment: ShipmentValidatable) => {
        if (shipment) {
          return packageCountExceededValidator(
            shipment,
            this.config.availablePackageCount(),
            this.config.listShipmentEnabled()
          );
        }
        return null;
      }
    ];
  }

  /**
   * Override in subclasses to add additional omit rules.
   */
  protected getOmitRules(): OmitRule<ShipmentValidatable>[] {
    return [
      {
        field: 'shipmentVehicleDTO',
        condition: () => !this.config.isPreShipment(),
      },
      {
        field: 'packages',
        condition: () => this.config.isPreShipment(),
      },
      {
        field: 'productCustomsClearanceDTOS',
        condition: () => true,
      }
    ];
  }

  protected getPickupValidators(): ValidatorSchema<HandoverDtoModel> {
    return {
      address: {
        city: [requiredString],
        postalCode: [requiredString],
      },
      namedLocationVO: requiredObject,
      timeWindowDto: (dateWindow: TimeWindowDtoModel | null | undefined): ValidationErrors | null => {
        return {
          ...[
            requiredObject(dateWindow?.timeWindowStart, 'startRequired'),
            isDateTimeWithTimezoneInPast(dateWindow?.timeWindowStart, this.config.dateOffset()),
            isDateTimeWithTimezoneInPast(dateWindow?.timeWindowEnd, this.config.dateOffset())
          ]
            .filter((error) => !!error)
            .reduce((acc, error) => ({ ...acc, ...error }), {}),
        };
      },
      contact: {
        name: [requiredString, maxStringLengthValidator(MAXIMUM_DEFAULT_STRING_INPUT_LENGTH)],
        phoneNumber: [requiredString, maxStringLengthValidator(20)],
      },
      $omit: [
        {
          field: 'namedLocationVO',
          condition: (value: any) => !!value?.address,
        },
        {
          field: 'address',
          condition: (value: any) => !!value?.namedLocationVO,
        },
        {
          field: 'contact',
          condition: (value: any) => !!value?.namedLocationPartnerContactVO,
        }
      ],
    } as ValidatorSchema<HandoverDtoModel>;
  }

  protected getDeliveryValidators(): ValidatorSchema<HandoverDtoModel> {
    return {
      address: {
        city: [requiredString],
        postalCode: [requiredString],
      },
      namedLocationVO: requiredObject,
      timeWindowDto: (dateWindow: TimeWindowDtoModel | null | undefined): ValidationErrors | null => {
        return {
          ...[
            requiredObject(dateWindow?.timeWindowStart, 'startRequired'),
            isDateTimeWithTimezoneInPast(dateWindow?.timeWindowStart, this.config.dateOffset()),
            isDateTimeWithTimezoneInPast(dateWindow?.timeWindowEnd, this.config.dateOffset())
          ]
            .filter((error) => !!error)
            .reduce((acc, error) => ({ ...acc, ...error }), {}),
        };
      },
      contact: {
        name: [requiredString, maxStringLengthValidator(MAXIMUM_DEFAULT_STRING_INPUT_LENGTH)],
        phoneNumber: [requiredString, maxStringLengthValidator(20)],
      },
      $omit: [
        {
          field: 'namedLocationVO',
          condition: (value: any) => !!value?.address,
        },
        {
          field: 'address',
          condition: (value: any) => !!value?.namedLocationVO,
        },
        {
          field: 'contact',
          condition: (value: any) => !!value?.namedLocationPartnerContactVO,
        }
      ],
    } as ValidatorSchema<HandoverDtoModel>;
  }

  protected getPackagesValidators(): ValidatorSchema<PackageDtoModel> {
    return {
      height: [requiredNumber, minNumber(1)],
      width: [requiredNumber, minNumber(1)],
      length: [requiredNumber, minNumber(1)],
      weight: [requiredNumber, greaterThan(0)],
      pieces: [requiredNumber, minNumber(1)],
    };
  }

  protected getProductCustomsClearanceValidators(): ValidatorSchema<ProductCustomsClearanceDtoModel> {
    return {
      totalWeight: [greaterThan(0)],
    };
  }

  protected getShipmentVehicleValidators(): ValidatorSchema<ShipmentVehicleDtoModel> {
    return {
      vehicleType: [requiredString],
    };
  }
}

/**
 * Factory function for backward compatibility.
 * Use ShipmentDtoValidatorsClass for extensibility.
 */
export const shipmentDtoValidators = (config: ShipmentConfig): ValidatorSchema<ShipmentValidatable> => {
  return new ShipmentDtoValidatorsClass(config).getSchema();
}
